import deepmerge from "deepmerge";
// types
import type { MergeDeep } from "type-fest";

export const merge = (...params: Array<object>): object => (Object.assign({}, ...params));

export const mergeDeep = <O1, O2>(obj1: O1, obj2: O2): MergeDeep<O1, O2> => (
	deepmerge(obj1, obj2, { arrayMerge: (_destinationArray, sourceArray) => (sourceArray) })
);

const BYTE_UNITS = ["byte", "kilobyte", "megabyte", "gigabyte", "terabyte", "petabyte"] as const satisfies ReadonlyArray<Intl.NumberFormatOptions["unit"]>;

export const formatBytes = (bytes: number, decimals: number = 2) => {
	const k = 1000;
	const i = (bytes === 0) ? 0 : Math.min(Math.floor(Math.log(bytes) / Math.log(k)), BYTE_UNITS.length - 1);
	const LOCALE = new Intl.Locale(new Intl.NumberFormat().resolvedOptions().locale);
	return new Intl.NumberFormat(LOCALE.baseName, {
		style: "unit",
		unit: BYTE_UNITS[i] ?? "byte",
		unitDisplay: "short",
		maximumFractionDigits: decimals,
	}).format(bytes / Math.pow(k, i));
};

export const compareObjects = (o1: object, o2: object): boolean => {
	for (const p in o1) {
		if (o1.hasOwnProperty(p)) {
			if (o1[p] !== o2[p]) {
				return false;
			}
		}
	}
	for (const p in o2) {
		if (o2.hasOwnProperty(p)) {
			if (o1[p] !== o2[p]) {
				return false;
			}
		}
	}
	return true;
};

export const roundToPrecision = (number: number, precision: number = 0.1): number => (
	Math.round(number / precision) / (1 / precision) // Is a hack for correct rounding of: Math.round(number / precision) * precision
);

export const getNumberRange = (min: number, max: number, step: number = 1): Array<number> => {
	const numberRange: Array<number> = [];
	// We have to round here because of how JavaScript handles decimal values. (0.2 + 0.1 = 0.30000000000000004)
	for (let i = min; i <= max; i = roundToPrecision(i + step, step)) {
		numberRange.push(i);
	}
	return numberRange;
};

export const decimal2Hex = (decimal: number, length: number | undefined = undefined, signed: boolean = false): string => {
	const value = (typeof length === "number" && (signed || decimal < 0)) ? decimal & (Math.pow(16, length) - 1) : decimal;
	const hex = value.toString(16).toUpperCase();
	if (length === undefined) {
		return hex;
	}
	return hex.padStart(length, "0");
};

export const isRocBuildUrl = () => (globalThis.location.origin === "https://rocbuild.blob.core.windows.net");

export const getRefererUrl = () => (`${globalThis.location.origin}${globalThis.location.pathname}${globalThis.location.search}`);

export const BBCODE_BOLD_REGEX = /\[b](.+?)\[\/b]/;
